import { Navigate, Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import Sidebar from "./../global/Sidebar/Sidebar";
import TopBar from "./../global/Topbar/Topbar";
import Breadcrumb from "../components/breadcrum/Breadcrum";
import { tokenExpTime, selectTimeoutEnabled, setTimeoutSettings } from "../redux/auth/authSlice";
import { useAppSelector, useAppDispatch } from "../redux/hooks";
import useSessionGuard from "../Hooks/useSessionGuard";
import useIdleTimeout from "../Hooks/useIdleTimeout";
import SessionTimeoutWarning from "../components/SessionTimeoutWarning/SessionTimeoutWarning";
import { useState, useEffect, useRef } from "react";
import Loading from "../components/LoadingPage/Loading";
import { useGetSettingsMutation } from "../redux/settings/settingsApiSlice";
import useSettings from "../Hooks/useSettings";
import { selectIsSettingsLoaded, SettingName } from "../redux/settings/settingsSlice";

const PrivateRoute = () => {
  const dispatch = useAppDispatch();
  const tokenExpiryTime = useAppSelector(tokenExpTime);
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const isSessionTimeoutEnabled = useAppSelector(selectTimeoutEnabled);
  const isSettingsLoaded = useAppSelector(selectIsSettingsLoaded);
  const { showWarning, remainingTime, resetTimer, handleLogout } = useIdleTimeout();
  const { isChecking } = useSessionGuard(tokenExpiryTime);
  const [getSettings] = useGetSettingsMutation();
  const { refreshSystemSettingsAPI } = useSettings();
  const hasFetchedRef = useRef(false);
  const hasFetchedSystemSettingsRef = useRef(false);

  // Fetch TimeoutSettings from the settings API
  useEffect(() => {
    if (!isLoggedIn || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    getSettings({ searchText: SettingName.TIMEOUT_SETTINGS })
      .unwrap()
      .then((res: any) => {
        const setting = res?.result?.find(
          (s: any) => s.settingName === SettingName.TIMEOUT_SETTINGS
        );
        if (setting?.settingValue) {
          const parsed = JSON.parse(setting.settingValue);
          dispatch(setTimeoutSettings({
            timeoutEnabled: parsed.IsEnabled ?? false,
            timeoutMinutes: parsed.TimeoutMinutes ?? 20,
          }));
        }
      })
      .catch(() => {
        // If API fails, keep using values from JWT token
      });
  }, [isLoggedIn, getSettings, dispatch]);

  // Fetch all system settings in-memory for module access control
  useEffect(() => {
    if (!isLoggedIn || hasFetchedSystemSettingsRef.current) return;
    hasFetchedSystemSettingsRef.current = true;

    refreshSystemSettingsAPI();
  }, [isLoggedIn, refreshSystemSettingsAPI]);

  const [isCollapsed, setIsCollapsed] = useState(
    localStorage.getItem("sidebarCollapsed") === "true"
  );

  const toggleCollapsed = () => {
    localStorage.setItem("sidebarCollapsed", (!isCollapsed).toString());
    setIsCollapsed(!isCollapsed);
  };

  if (isChecking || (isLoggedIn && !isSettingsLoaded)) return <Loading />;

  return isLoggedIn ? (
    <div className={`main-wrapper ${isCollapsed ? "sidebarOpen" : ""}`}>
      <TopBar isCollapsed={isCollapsed} toggleCollapsed={toggleCollapsed} />
      <div className="main" style={{ display: "flex", flex: 1 }}>
        <Sidebar isCollapsed={isCollapsed} />
        <div className="content-wrapper">
          <Breadcrumb />
          <Outlet />
        </div>
      </div>

      {isSessionTimeoutEnabled && (
        <SessionTimeoutWarning
          show={showWarning}
          remainingTime={remainingTime}
          onStayLoggedIn={resetTimer}
          onLogout={handleLogout}
        />
      )}
    </div>
  ) : (
    <Navigate to="/login" replace />
  );
};

export default PrivateRoute;